/** @format */

import {useState} from 'react';

import {Box, Collapse} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import {OauthProvider} from 'graphql/generated/types';

import {providerIconSrc} from 'helpers/imageHelpers';

import {AnalyticsSystemModal} from './components/AnalyticsSystemModal';
import {
  Container,
  ContentRow,
  InstructionBlock,
  InstructionText,
  EmergencyIcon,
  SafetyBlock,
  SafetyTextColumn,
  SafetyTitle,
  SafetyText,
  SafetyIconWrapper,
  ConnectButton,
  ButtonIcon,
  HelpAccordion,
  HelpHeader,
  HelpHeaderTitle,
  HelpDropdownIcon,
  HelpContent,
  NumberedList,
  NumberedListItem,
  NumberedListIcon,
  NumberedListText,
  HelpEmergencyBlock,
  HelpEmergencyTextColumn,
  HelpEmergencyText,
  ReportAnalyticsButton,
} from './styles';

export const MetrikaStepHeader = () => (
  <Box component='span' sx={{display: 'inline-flex', gap: 1, alignItems: 'center'}}>
    3. <Trans>Яндекс Метрика</Trans>
    <Box component='img' src={providerIconSrc('METRIKA', 20)} alt='' sx={{width: 22, height: 22}} />
  </Box>
);

interface Props {
  onConnect: (provider: OauthProvider) => void;
}

const MetrikaConnectBlock = ({onConnect}: Props) => {
  const {t} = useTranslation();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleToggleDetails = () => {
    setIsDetailsOpen(prev => !prev);
  };

  const handleConnect = () => {
    onConnect('METRIKA');
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <Container data-testid='metrika-connect-block'>
      <ContentRow>
        <InstructionBlock>
          <InstructionText>
            <Trans>
              Для полноты картины нашему движку нужны данные из Яндекс Метрики с теми же кампаниями.
            </Trans>
          </InstructionText>

          <HelpAccordion isExpanded={isDetailsOpen}>
            <HelpHeader onClick={handleToggleDetails} data-testid='details-toggle'>
              <HelpHeaderTitle>
                <Trans>Подробнее о том, зачем нам нужен доступ к Метрике</Trans>
              </HelpHeaderTitle>
              <HelpDropdownIcon component='span' className='material-symbols-outlined'>
                {isDetailsOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
              </HelpDropdownIcon>
            </HelpHeader>
            <Collapse in={isDetailsOpen}>
              <HelpContent data-testid='details-content'>
                <InstructionText>
                  <Trans>
                    Мы сравниваем данные из рекламного кабинета с данными из Яндекс Метрики.
                  </Trans>
                </InstructionText>
                <InstructionText>
                  <Trans>Это позволяет нам:</Trans>
                </InstructionText>
                <NumberedList>
                  <NumberedListItem>
                    <NumberedListIcon component='span' className='material-symbols-outlined'>
                      counter_1
                    </NumberedListIcon>
                    <NumberedListText>
                      <Trans>видеть результативность рекламных кампаний</Trans>
                    </NumberedListText>
                  </NumberedListItem>
                  <NumberedListItem>
                    <NumberedListIcon component='span' className='material-symbols-outlined'>
                      counter_2
                    </NumberedListIcon>
                    <NumberedListText>
                      <Trans>анализировать влияние внесенных в кампании изменений</Trans>
                    </NumberedListText>
                  </NumberedListItem>
                  <NumberedListItem>
                    <NumberedListIcon component='span' className='material-symbols-outlined'>
                      counter_3
                    </NumberedListIcon>
                    <NumberedListText>
                      <Trans>анализировать влияние внешних событий</Trans>
                    </NumberedListText>
                  </NumberedListItem>
                </NumberedList>
                <HelpEmergencyBlock>
                  <EmergencyIcon component='span' className='material-symbols-outlined'>
                    emergency
                  </EmergencyIcon>
                  <HelpEmergencyTextColumn>
                    <HelpEmergencyText>
                      <Trans>
                        Сейчас мы работаем только с Яндекс Метрикой. Если вы используете другую
                        систему аналитики, пожалуйста, сообщите нам и мы предложим вам варианты.
                      </Trans>
                    </HelpEmergencyText>
                    <ReportAnalyticsButton onClick={handleOpenModal}>
                      <Trans>Сообщить о своей системе аналитики</Trans>
                    </ReportAnalyticsButton>
                  </HelpEmergencyTextColumn>
                </HelpEmergencyBlock>
              </HelpContent>
            </Collapse>
          </HelpAccordion>

          <InstructionText>
            <Trans>
              Процесс подключения аналогичен. Разрешите доступ и мы начнем анализ данных.
            </Trans>
          </InstructionText>
        </InstructionBlock>

        <SafetyBlock>
          <SafetyTextColumn>
            <SafetyTitle>
              <Trans>Это безопасно</Trans>
            </SafetyTitle>
            <SafetyText>
              <Trans>
                Мы работаем только с данными и не изменяем настройки системы аналитики. Доступ
                нужен, чтобы корректно считать показатели и находить точки роста.
              </Trans>
            </SafetyText>
          </SafetyTextColumn>
          <SafetyIconWrapper component='span' className='material-symbols-outlined'>
            encrypted
          </SafetyIconWrapper>
        </SafetyBlock>
      </ContentRow>

      <ConnectButton onClick={handleConnect}>
        <ButtonIcon component='span' className='material-symbols-outlined'>
          arrow_outward
        </ButtonIcon>
        {t('Выдать доступ в окне {{provider}}', {provider: 'Яндекса'})}
      </ConnectButton>

      <AnalyticsSystemModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </Container>
  );
};

export default MetrikaConnectBlock;
